import React, { useState, useEffect } from 'react';
import { directoryAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Users,
  Search,
  Phone,
  MessageSquare,
  ChevronRight,
  Loader2,
  BookOpen,
  Mail,
  MapPin,
  Calendar,
  Heart,
  Lock
} from 'lucide-react';
import BottomNav from '../components/BottomNav';

const DirectoryPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const data = await directoryAPI.getAll();
      setMembers(data);
    } catch (error) {
      console.error('Error fetching directory:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMembers = members.filter(member => {
    const fullName = `${member.firstName} ${member.lastName}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  // Format date for display (Month Day, Year)
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    // If date is in YYYY-MM-DD format, parse it as local date
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    }
    // Handle ISO date strings
    if (dateStr.includes('T')) {
      const datePart = dateStr.split('T')[0];
      const [year, month, day] = datePart.split('-');
      return new Date(year, month - 1, day).toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    }
    return dateStr;
  };

  const getRoleBadge = (role) => {
    const roleStyles = {
      pastor: 'bg-tan-500 text-white',
      admin: 'bg-navy-900 text-white',
      deacon: 'bg-navy-700 text-white',
      minister: 'bg-navy-600 text-white',
      member: 'bg-gray-200 text-gray-700'
    };
    return roleStyles[role] || roleStyles.member;
  };

  const formatRole = (role) => {
    const roleLabels = {
      pastor: 'Pastor',
      admin: 'Admin',
      deacon: 'Deacon',
      minister: 'Minister',
      member: 'Member'
    };
    return roleLabels[role] || 'Member';
  };

  const handleSendMessage = (memberId) => {
    // Navigate to direct message page
    navigate(`/dm/${memberId}`);
    setSelectedMember(null);
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const getBackendUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || '';
  };

  // Helper to construct proper avatar URL from various formats
  const getAvatarUrl = (avatar) => {
    if (!avatar) return null;
    // Handle Cloudinary URLs (new cloud storage format)
    if (avatar.includes('cloudinary.com') || avatar.includes('res.cloudinary.com')) {
      return avatar;
    }
    // Handle relative URLs (legacy local format)
    if (avatar.startsWith('/api/uploads/')) {
      return `${getBackendUrl()}${avatar}`;
    }
    // Handle legacy absolute URLs from different environments
    // Extract just the path and use current backend URL
    if (avatar.includes('/api/uploads/')) {
      const uploadPath = avatar.substring(avatar.indexOf('/api/uploads/'));
      return `${getBackendUrl()}${uploadPath}`;
    }
    // Fallback for any other format (including full URLs)
    return avatar.startsWith('http') ? avatar : `${getBackendUrl()}${avatar}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header with gradient */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-5 pt-8 pb-8 rounded-b-3xl shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center backdrop-blur-sm">
            <Users className="w-6 h-6 text-tan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Church Directory</h1>
            <p className="text-tan-400 text-sm font-medium">{members.length} members</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Search members..."
            className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-xl backdrop-blur-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Member List */}
      <div className="px-4 -mt-4">
        <Card className="border-0 shadow-lg rounded-2xl overflow-hidden">
          <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
            <div className="divide-y divide-gray-100">
              {filteredMembers.map((member) => (
                <div
                  key={member.id}
                  className="p-4 hover:bg-gray-50 transition-colors cursor-pointer active:bg-gray-100"
                  onClick={() => setSelectedMember(member)}
                >
                  <div className="flex items-center gap-3">
                    {/* Profile Photo or Initials */}
                    {member.avatar ? (
                      <img 
                        src={getAvatarUrl(member.avatar)}
                        alt={`${member.firstName} ${member.lastName}`}
                        className="w-12 h-12 rounded-full object-cover"
                        onError={(e) => {
                          // Hide broken image and show initials instead
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-12 h-12 rounded-full bg-navy-100 items-center justify-center ${member.avatar ? 'hidden' : 'flex'}`}
                    >
                      <span className="text-navy-900 font-bold">
                        {member.firstName?.charAt(0)}{member.lastName?.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-navy-900">
                          {member.firstName} {member.lastName}
                        </p>
                        <Badge className={`text-xs ${getRoleBadge(member.role)}`}>
                          {formatRole(member.role)}
                        </Badge>
                      </div>
                      {/* Phone Number - Clickable to dial */}
                      {member.phone && (
                        <a 
                          href={`tel:${member.phone.replace(/[^0-9+]/g, '')}`}
                          className="text-sm text-gray-500 flex items-center gap-1 hover:text-navy-900"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Phone className="w-3 h-3" />
                          {formatPhoneNumber(member.phone)}
                        </a>
                      )}
                      {/* Favorite Scripture - truncated */}
                      {member.favoriteScripture && (
                        <p className="text-xs text-tan-600 truncate italic mt-1">
                          &ldquo;{member.favoriteScripture.substring(0, 50)}{member.favoriteScripture.length > 50 ? '...' : ''}&rdquo;
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-tan-500" />
                  </div>
                </div>
              ))}

              {filteredMembers.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No members found</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Member Profile Dialog */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-navy-900">Member Profile</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              <div className="flex flex-col items-center py-4">
                {selectedMember.avatar ? (
                  <img 
                    src={getAvatarUrl(selectedMember.avatar)}
                    alt={`${selectedMember.firstName} ${selectedMember.lastName}`}
                    className="w-20 h-20 rounded-full object-cover mb-3"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`w-20 h-20 rounded-full bg-navy-100 items-center justify-center mb-3 ${selectedMember.avatar ? 'hidden' : 'flex'}`}
                >
                  <span className="text-2xl font-bold text-navy-900">
                    {selectedMember.firstName?.charAt(0)}{selectedMember.lastName?.charAt(0)}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-navy-900">
                  {selectedMember.firstName} {selectedMember.lastName}
                </h3>
                <Badge className={`mt-1 ${getRoleBadge(selectedMember.role)}`}>
                  {formatRole(selectedMember.role)}
                </Badge>
              </div>

              <div className="space-y-3">
                {/* Private Profile Notice */}
                {selectedMember.privateProfile && !['admin', 'pastor', 'deacon'].includes(user?.role) && (
                  <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-xl text-gray-500">
                    <Lock className="w-4 h-4" />
                    <span className="text-sm">This member has a private profile</span>
                  </div>
                )}
                
                {/* Phone Number with Call Button */}
                {selectedMember.phone && (
                  <a 
                    href={`tel:${selectedMember.phone.replace(/[^0-9+]/g, '')}`}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-navy-900">Call</p>
                      <p className="text-sm text-gray-600">{formatPhoneNumber(selectedMember.phone)}</p>
                    </div>
                  </a>
                )}

                {/* Email - for privileged users */}
                {selectedMember.email && ['admin', 'pastor', 'deacon'].includes(user?.role) && (
                  <a 
                    href={`mailto:${selectedMember.email}`}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-navy-900">Email</p>
                      <p className="text-sm text-gray-600">{selectedMember.email}</p>
                    </div>
                  </a>
                )}

                {/* Address - for privileged users */}
                {selectedMember.mailingAddress && ['admin', 'pastor', 'deacon'].includes(user?.role) && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-navy-900">Address</p>
                      <p className="text-sm text-gray-600">{selectedMember.mailingAddress}</p>
                    </div>
                  </div>
                )}

                {/* Birthday & Anniversary - for privileged users */}
                {['admin', 'pastor', 'deacon'].includes(user?.role) && (selectedMember.birthday || selectedMember.weddingAnniversary) && (
                  <div className="grid grid-cols-2 gap-2">
                    {selectedMember.birthday && (
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                        <Calendar className="w-4 h-4 text-tan-600" />
                        <div>
                          <p className="text-xs text-gray-500">Birthday</p>
                          <p className="text-sm font-medium text-navy-900">{formatDateDisplay(selectedMember.birthday)}</p>
                        </div>
                      </div>
                    )}
                    {selectedMember.weddingAnniversary && (
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                        <Heart className="w-4 h-4 text-red-500" />
                        <div>
                          <p className="text-xs text-gray-500">Anniversary</p>
                          <p className="text-sm font-medium text-navy-900">{formatDateDisplay(selectedMember.weddingAnniversary)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Favorite Scripture */}
                {selectedMember.favoriteScripture && (
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-5 h-5 text-tan-600" />
                      <span className="text-sm font-medium text-navy-900">Favorite Scripture</span>
                    </div>
                    <p className="text-sm text-gray-700 italic">&ldquo;{selectedMember.favoriteScripture}&rdquo;</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {selectedMember.id !== user?.id && (
                <div className="flex gap-3 pt-2">
                  {selectedMember.phone && (
                    <a
                      href={`tel:${selectedMember.phone.replace(/[^0-9+]/g, '')}`}
                      className="flex-1 flex items-center justify-center gap-2 h-11 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      Call
                    </a>
                  )}
                  <Button
                    className={`${selectedMember.phone ? 'flex-1' : 'w-full'} h-11 bg-navy-900 hover:bg-navy-800 text-white rounded-xl`}
                    onClick={() => handleSendMessage(selectedMember.id)}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Message
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default DirectoryPage;
